import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { api, TOKEN_KEY, USER_KEY, setUnauthorizedHandler } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(() => {
        try {
            const raw = localStorage.getItem(USER_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    });
    // three states: null = checking, {..} = user, false = not-authed
    const [status, setStatus] = useState(
        localStorage.getItem(TOKEN_KEY) ? "checking" : "unauthenticated",
    );
    // Phase 7b — customer favorites cache (target_type=POST). Only populated
    // for CUSTOMER users; ignored otherwise.
    const [favIds, setFavIds] = useState(() => new Set());
    const favLoadedFor = useRef(null);

    const persistSession = useCallback((tokenResp) => {
        localStorage.setItem(TOKEN_KEY, tokenResp.access_token);
        localStorage.setItem(USER_KEY, JSON.stringify(tokenResp.user));
        setUser(tokenResp.user);
        setStatus("authenticated");
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch {
            /* ignore */
        }
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setUser(null);
        setStatus("unauthenticated");
        setFavIds(new Set());
        favLoadedFor.current = null;
        window.location.href = "/login";
    }, []);

    const refresh = useCallback(async () => {
        try {
            const res = await api.get("/auth/me");
            localStorage.setItem(USER_KEY, JSON.stringify(res.data));
            setUser(res.data);
            setStatus("authenticated");
            return res.data;
        } catch (e) {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
            setUser(null);
            setStatus("unauthenticated");
            throw e;
        }
    }, []);

    // Phase 7b — one-shot fetch of the customer's favorite ids
    const loadFavIds = useCallback(async (customerId) => {
        if (favLoadedFor.current === customerId) return;
        try {
            const r = await api.get("/me/favorites/ids?target_type=POST");
            setFavIds(new Set(r.data?.ids || []));
            favLoadedFor.current = customerId;
        } catch {
            setFavIds(new Set());
        }
    }, []);

    const isFavorited = useCallback((postId) => favIds.has(postId), [favIds]);

    /** Optimistic toggle. Returns {favorited, count_for_target}. Rolls back on error. */
    const toggleFavorite = useCallback(async (postId) => {
        const prev = favIds.has(postId);
        setFavIds((s) => {
            const n = new Set(s);
            if (prev) n.delete(postId); else n.add(postId);
            return n;
        });
        try {
            const r = await api.post("/me/favorites/toggle", { target_type: "POST", target_id: postId });
            // Reconcile in case server disagreed (e.g. race with another tab).
            setFavIds((s) => {
                const n = new Set(s);
                if (r.data?.favorited) n.add(postId); else n.delete(postId);
                return n;
            });
            return r.data;
        } catch (e) {
            // Roll back
            setFavIds((s) => {
                const n = new Set(s);
                if (prev) n.add(postId); else n.delete(postId);
                return n;
            });
            throw e;
        }
    }, [favIds]);

    // Give the axios interceptor a way to reset our React state on 401.
    useEffect(() => {
        setUnauthorizedHandler(() => {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
            setUser(null);
            setStatus("unauthenticated");
            setFavIds(new Set());
            favLoadedFor.current = null;
            if (window.location.pathname !== "/login") {
                window.location.href = "/login";
            }
        });
    }, []);

    useEffect(() => {
        if (localStorage.getItem(TOKEN_KEY)) {
            refresh().catch(() => {});
        }
    }, [refresh]);

    // When the current user is a CUSTOMER, warm the favorite ids cache once.
    useEffect(() => {
        if (user?.role === "CUSTOMER" && status === "authenticated") {
            loadFavIds(user.id);
        }
    }, [user, status, loadFavIds]);

    const value = useMemo(
        () => ({
            user, status, persistSession, logout, refresh,
            isFavorited, toggleFavorite, favIds, loadFavIds,
        }),
        [user, status, persistSession, logout, refresh, isFavorited, toggleFavorite, favIds, loadFavIds],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
};
